import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SocialMediaAuditFactory } from '../../services/socialMediaAudit/socialMediaAuditFactory';
import {
  InstagramAuditResult,
  InstagramPublicData,
  InstagramInsightsData,
} from '../../types/socialMediaAudit';
import { InstagramAuditForm } from '../../components/socialMedia/InstagramAuditForm';
import { InstagramAuditResults } from '../../components/socialMedia/InstagramAuditResults';
import { useContent } from '../../hooks/useContent';
import { useSeo } from '../../hooks/useSeo';
import { defaultPageContent } from '../../contexts/DefaultPageContent';

const PLATFORMS = [
  { id: 'instagram', name: 'Instagram Audit', status: 'Active', active: true },
  { id: 'tiktok', name: 'TikTok Audit', status: 'Roadmap', active: false },
  { id: 'linkedin', name: 'LinkedIn Audit', status: 'Roadmap', active: false },
  { id: 'youtube', name: 'YouTube Audit', status: 'Roadmap', active: false },
  { id: 'facebook', name: 'Facebook Audit', status: 'Roadmap', active: false },
  { id: 'x', name: 'X (Twitter) Audit', status: 'Roadmap', active: false },
];

export const InstagramAuditPage: React.FC = () => {
  const { content } = useContent();
  const pageContent = content.socialMediaAudit || defaultPageContent.socialMediaAudit;

  const onSeoApplied = useCallback(() => {
    window.dispatchEvent(new Event('app-rendered'));
  }, []);

  useSeo(pageContent, 'socialMediaAudit', onSeoApplied);

  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<InstagramAuditResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAudit = async (
    profile: InstagramPublicData,
    insights?: InstagramInsightsData,
    competitor?: InstagramPublicData
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      await new Promise((resolve) => setTimeout(resolve, 450));

      const auditResult = (await SocialMediaAuditFactory.performAudit('instagram', {
        profileData: profile,
        insightsData: insights,
        competitorData: competitor,
      })) as InstagramAuditResult;

      setResult(auditResult);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err.message || 'An error occurred during the deterministic audit.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      {/* Hero Banner */}
      <section className="bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 text-white py-14 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <span className="inline-block px-3.5 py-1 rounded-md bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-xs font-bold uppercase tracking-widest mb-3">
            100% Deterministic • Zero AI / Zero External API Dependency
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">
            Social Media & Instagram Brand Audit
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Evaluate how effectively your brand presents itself on Instagram using our proprietary 6-pillar consulting methodology. Every score is reproducible, mathematically explainable, and exportable as a client-ready white-label PDF report.
          </p>

          {/* Multi-Platform Ecosystem Bar */}
          <div className="flex flex-wrap justify-center gap-2 mt-8">
            {PLATFORMS.map((p) => (
              <div
                key={p.id}
                className={`px-3.5 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 border ${
                  p.active
                    ? 'bg-indigo-600 border-indigo-400 text-white shadow-md shadow-indigo-600/30'
                    : 'bg-white/5 border-white/10 text-slate-400'
                }`}
              >
                <span>{p.name}</span>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${
                    p.active ? 'bg-white/20 text-white' : 'bg-white/5 text-slate-400'
                  }`}
                >
                  {p.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Workspace */}
      <div className="max-w-5xl mx-auto px-4 -mt-6">
        {error && (
          <div className="bg-rose-50 text-rose-700 p-4 rounded-xl mb-6 border border-rose-200 text-sm font-medium">
            {error}
          </div>
        )}

        {!result ? (
          <InstagramAuditForm onAudit={handleAudit} isLoading={isLoading} />
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between bg-white px-5 py-3.5 rounded-xl border border-slate-200 shadow-sm">
              <button
                type="button"
                onClick={() => setResult(null)}
                className="text-indigo-600 font-bold text-sm hover:underline flex items-center gap-1.5"
              >
                <span>← Audit Another Instagram Profile</span>
              </button>
              <Link
                to="/brand-audit/"
                className="text-xs font-semibold text-slate-600 hover:text-indigo-600"
              >
                Run Linguistic Brand Name Audit →
              </Link>
            </div>

            <InstagramAuditResults result={result} />
          </div>
        )}
      </div>
    </div>
  );
};
